import { useCallback, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Logo from './imagens/logo_original.png';
import HeroImage from './imagens/hero-mulher.png';
import Card1 from './imagens/card1.jpg';
import Card2 from './imagens/card2.jpg';
import Card3 from './imagens/card3.jpg';
import Card4 from './imagens/card4.jpg';

/* ── Feature panel (idêntico ao login/cadastro) ── */
function FeaturePanel() {
  const cards = [
    { src: Card1, rotate: -12, top: '5%',   left: '-12%',  delay: 0.3 },
    { src: Card2, rotate: 10,  top: '2%',   right: '-30%', delay: 0.45 },
    { src: Card3, rotate: -6,  top: '45%',  left: '-13%',  delay: 0.6 },
    { src: Card4, rotate: 8,   top: '42%',  right: '-30%', delay: 0.75 },
  ];

  return (
    <section className="relative flex h-full flex-col items-center justify-center overflow-inherit py-8 lg:py-0">
      <div className="relative mx-auto w-full max-w-sm">
        {cards.map((card, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.7, rotate: card.rotate * 0.5 }}
            animate={{ opacity: 1, scale: 1, rotate: card.rotate }}
            transition={{ duration: 0.6, delay: card.delay, type: 'spring', stiffness: 80 }}
            className="absolute z-0"
            style={{
              top: card.top,
              ...(card.left ? { left: card.left } : {}),
              ...(card.right ? { right: card.right } : {}),
              width: '42%',
            }}
          >
            <img
              src={card.src}
              alt=""
              className="w-full rounded-xl shadow-lg"
              style={{
                border: '3px solid rgba(255,255,255,0.6)',
                boxShadow: '0 8px 30px rgba(63, 4, 92, 0.2)',
              }}
            />
          </motion.div>
        ))}

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative z-10"
        >
          <img
            src={HeroImage}
            alt="Mulher acessando a plataforma VídeoOferta no celular"
            className="mx-auto w-full max-w-xs drop-shadow-2xl sm:max-w-sm"
            style={{
              filter: 'drop-shadow(0 20px 40px rgba(63, 4, 92, 0.18))',
              maskImage: 'linear-gradient(to bottom, black 65%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to bottom, black 81%, transparent 100%)',
            }}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="absolute z-20"
          style={{ top: '61%', left: '20%' }}
        >
          <h1 className="hero-title">Crie ofertas em vídeo</h1>
        </motion.div>

        {/* Badge: Templates prontos */}
        <motion.div
          initial={{ opacity: 0, x: -20, y: -10 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5, type: 'spring', stiffness: 120 }}
          className="absolute left-0 top-6 z-30 sm:-left-4 lg:-left-6"
        >
          <div className="flex items-center gap-2.5 rounded-2xl border border-flow-border bg-white px-4 pt-[2px] pb-[2px] shadow-lg">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-flow-brand-soft text-flow-brand">
              <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
                <path d="M4 5h16v14H4z" fill="none" stroke="currentColor" strokeWidth="2" />
                <path d="M7 9h10M7 13h6" stroke="currentColor" strokeWidth="2" />
              </svg>
            </span>
            <span className="text-sm font-semibold text-flow-ink">Templates prontos</span>
          </div>
        </motion.div>

        {/* Badge: Baixe e publique */}
        <motion.div
          initial={{ opacity: 0, x: 20, y: 10 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7, type: 'spring', stiffness: 120 }}
          className="absolute right-0 top-6 md:top-auto md:bottom-16 z-30 lg:-right-6"
        >
          <div className="flex items-center gap-2.5 rounded-2xl border border-flow-border bg-white px-4 pt-[2px] pb-[2px] shadow-lg">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-flow-brand-soft text-flow-brand">
              <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
                <path d="M12 3v12m0 0 4-4m-4 4-4-4M5 21h14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span className="text-sm font-semibold text-flow-ink">Baixe e publique</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ── Reset password form ── */
function ResetForm({ loading, onSubmit, message, setMessage }) {
  const [senha, setSenha] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [touched, setTouched] = useState({ senha: false, confirmar: false });
  const [submitted, setSubmitted] = useState(false);

  const errors = useMemo(() => {
    const next = {};
    if (!senha) {
      next.senha = 'Informe a nova senha.';
    } else if (senha.length < 6) {
      next.senha = 'Use ao menos 6 caracteres.';
    }
    if (!confirmar) {
      next.confirmar = 'Confirme a nova senha.';
    } else if (confirmar !== senha) {
      next.confirmar = 'As senhas não coincidem.';
    }
    return next;
  }, [senha, confirmar]);

  const isFormValid = Object.keys(errors).length === 0;
  const showSenhaError = (touched.senha || submitted) && errors.senha;
  const showConfirmarError = (touched.confirmar || submitted) && errors.confirmar;

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!isFormValid) {
      setMessage({ type: 'error', text: 'Revise os campos para continuar.' });
      return;
    }
    setMessage(null);
    onSubmit({ senha });
  };

  const inputClass = (hasError) =>
    `w-full rounded-xl border bg-white px-4 py-3 text-sm text-flow-ink placeholder-slate-400 outline-none transition focus-visible:ring-2 focus-visible:ring-flow-brand ${
      hasError
        ? 'border-red-400/70 focus-visible:ring-red-400'
        : 'border-flow-border focus-visible:border-flow-brand'
    }`;

  return (
    <form className="space-y-5" onSubmit={handleSubmit} noValidate>
      {/* Nova senha */}
      <div className="space-y-2">
        <label htmlFor="senha" className="text-sm font-medium text-flow-ink">
          Nova senha
        </label>
        <div className="relative">
          <input
            id="senha"
            name="senha"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            className={`${inputClass(showSenhaError)} pr-12`}
            placeholder="••••••••"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            onBlur={() => setTouched((p) => ({ ...p, senha: true }))}
            aria-invalid={Boolean(showSenhaError)}
            aria-describedby={showSenhaError ? 'senha-error' : undefined}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword((p) => !p)}
            className="absolute inset-y-0 right-3 flex items-center text-xs font-medium text-flow-muted transition hover:text-flow-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flow-brand"
            aria-pressed={showPassword}
            aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
          >
            {showPassword ? 'Ocultar' : 'Mostrar'}
          </button>
        </div>
        {showSenhaError && (
          <p id="senha-error" className="text-xs text-red-300">
            {errors.senha}
          </p>
        )}
      </div>

      {/* Confirmar senha */}
      <div className="space-y-2">
        <label htmlFor="confirmar" className="text-sm font-medium text-flow-ink">
          Confirmar senha
        </label>
        <div className="relative">
          <input
            id="confirmar"
            name="confirmar"
            type={showConfirm ? 'text' : 'password'}
            autoComplete="new-password"
            className={`${inputClass(showConfirmarError)} pr-12`}
            placeholder="••••••••"
            value={confirmar}
            onChange={(e) => setConfirmar(e.target.value)}
            onBlur={() => setTouched((p) => ({ ...p, confirmar: true }))}
            aria-invalid={Boolean(showConfirmarError)}
            aria-describedby={showConfirmarError ? 'confirmar-error' : undefined}
            required
          />
          <button
            type="button"
            onClick={() => setShowConfirm((p) => !p)}
            className="absolute inset-y-0 right-3 flex items-center text-xs font-medium text-flow-muted transition hover:text-flow-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flow-brand"
            aria-pressed={showConfirm}
            aria-label={showConfirm ? 'Ocultar senha' : 'Mostrar senha'}
          >
            {showConfirm ? 'Ocultar' : 'Mostrar'}
          </button>
        </div>
        {showConfirmarError && (
          <p id="confirmar-error" className="text-xs text-red-300">
            {errors.confirmar}
          </p>
        )}
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={!isFormValid || loading}
        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-flow-brand px-4 py-3 text-sm font-semibold text-white transition hover:bg-flow-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flow-brand disabled:cursor-not-allowed disabled:opacity-60"
        aria-disabled={!isFormValid || loading}
      >
        {loading ? (
          <>
            <span
              className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
              aria-hidden="true"
            />
            Salvando...
          </>
        ) : (
          'Definir nova senha'
        )}
      </button>

      {/* Messages */}
      {message && (
        <div
          className={`rounded-xl border px-4 py-3 text-sm ${
            message.type === 'error'
              ? 'border-red-400/40 bg-red-500/10 text-red-600'
              : 'border-flow-brand/30 bg-flow-brand-soft text-flow-brand'
          }`}
          role={message.type === 'error' ? 'alert' : 'status'}
          aria-live="polite"
        >
          {message.text}
        </div>
      )}

      <p className="text-center text-sm text-flow-muted">
        Lembrou sua senha?{' '}
        <Link
          to="/entrar"
          className="font-semibold text-flow-brand transition hover:text-flow-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flow-brand"
        >
          Entrar
        </Link>
      </p>
    </form>
  );
}

/* ── Success state ── */
function SuccessView() {
  return (
    <div className="space-y-5 text-center">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-flow-brand-soft">
        <svg
          viewBox="0 0 24 24"
          className="h-8 w-8 text-flow-brand"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20 6 9 17l-5-5" />
        </svg>
      </div>
      <div className="space-y-2">
        <h3 className="text-lg font-semibold text-flow-ink">Senha redefinida!</h3>
        <p className="text-sm text-flow-muted">
          Sua senha foi alterada com sucesso. Agora você pode fazer login com a nova senha.
        </p>
      </div>
      <Link
        to="/entrar"
        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-flow-brand px-4 py-3 text-sm font-semibold text-white transition hover:bg-flow-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flow-brand"
      >
        Ir para o login
      </Link>
    </div>
  );
}

/* ── Invalid token state ── */
function InvalidTokenView() {
  return (
    <div className="space-y-5 text-center">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
        <svg
          viewBox="0 0 24 24"
          className="h-8 w-8 text-red-500"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      </div>
      <div className="space-y-2">
        <h3 className="text-lg font-semibold text-flow-ink">Link inválido ou expirado</h3>
        <p className="text-sm text-flow-muted">
          O link de redefinição de senha é inválido ou já expirou. Solicite um novo.
        </p>
      </div>
      <div className="flex flex-col gap-3">
        <Link
          to="/forgot-password"
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-flow-brand px-4 py-3 text-sm font-semibold text-white transition hover:bg-flow-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flow-brand"
        >
          Solicitar novo link
        </Link>
        <Link
          to="/entrar"
          className="text-sm font-medium text-flow-brand transition hover:text-flow-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flow-brand"
        >
          Voltar para o login
        </Link>
      </div>
    </div>
  );
}

/* ── Page ── */
function ResetPasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [success, setSuccess] = useState(false);
  const abortRef = useRef(null);

  // Extrair token da URL: /reset-password?token=xxx
  const token = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('token') || '';
  }, [location.search]);

  const abortInFlight = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  const handleSubmit = async ({ senha }) => {
    if (!token) {
      setMessage({ type: 'error', text: 'Token de redefinição não encontrado.' });
      return;
    }

    abortInFlight();
    setMessage(null);
    setLoading(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch('https://videooferta.com/api/redefinir-senha.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, senha }),
        signal: controller.signal,
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();

      if (data.success) {
        setSuccess(true);
      } else {
        setMessage({
          type: 'error',
          text: data.error || 'Não foi possível redefinir a senha. O link pode ter expirado.',
        });
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      setMessage({ type: 'error', text: 'Erro ao conectar com o servidor.' });
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  };

  // Decide o conteúdo do card
  const renderCardContent = () => {
    if (success) return <SuccessView />;
    if (!token) return <InvalidTokenView />;
    return (
      <>
        <div className="mb-6 space-y-2">
          <h2 className="text-2xl font-semibold text-flow-ink">Definir nova senha</h2>
          <p className="text-sm text-flow-muted">
            Escolha uma nova senha para sua conta.
          </p>
        </div>
        <ResetForm
          loading={loading}
          onSubmit={handleSubmit}
          message={message}
          setMessage={setMessage}
        />
      </>
    );
  };

  return (
    <div className="relative min-h-screen bg-dot-grid overflow-hidden">
      <main className="relative mx-auto flex min-h-screen max-w-6xl flex-col gap-10 px-6 py-12 lg:flex-row lg:items-center">
        {/* Feature panel — left on desktop */}
        <div className="order-2 w-full lg:order-1 lg:w-1/2">
          <FeaturePanel />
        </div>

        {/* Form card — right on desktop */}
        <div className="order-1 w-full lg:order-2 lg:w-1/2">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="mx-auto w-full max-w-md rounded-2xl border border-flow-border bg-white p-6 sm:p-8"
          >
            <div className="mb-6 flex items-center justify-center">
              <img src={Logo} alt="VídeoOferta" className="h-12 w-auto" />
            </div>

            {renderCardContent()}
          </motion.div>
        </div>
      </main>
    </div>
  );
}

export default ResetPasswordPage;
